import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { supabase } from "../lib/supabase";

export interface Leader {
  id: number;
  name: string;
  title: string;
  govTitle?: string;
  image: string;
  bio: string;
  education: string;
  achievements: string[];
  sort_order?: number;
}

export interface Member {
  id: number;
  name: string;
  role: string;
  title?: string;
  image: string;
  sort_order?: number;
}

export interface Event {
  id: number;
  title: string;
  date: string;
  location: string;
  image: string;
  images: string[];
  description: string;
  sort_order?: number;
}

export interface DiasporaCoordinator {
  id: number;
  image: string;
  sort_order?: number;
}

interface AppState {
  isAuthenticated: boolean;
  isLoading: boolean;
  hasHydrated: boolean;
  leaders: Leader[];
  members: Member[];
  events: Event[];
  diasporaCoordinators: DiasporaCoordinator[];
  
  login: (email?: string, password?: string) => Promise<boolean>;
  logout: () => Promise<void>;
  
  setHasHydrated: (state: boolean) => void;
  fetchData: () => Promise<void>;
  
  addLeader: (leader: Omit<Leader, 'id' | 'sort_order'>) => Promise<boolean>;
  updateLeader: (id: number, leader: Partial<Leader>) => Promise<boolean>;
  removeLeader: (id: number) => Promise<void>;
  updateLeaderSortOrder: (id: number, sortOrder: number) => Promise<void>;
  
  addMember: (member: Omit<Member, 'id' | 'sort_order'>) => Promise<boolean>;
  updateMember: (id: number, member: Partial<Member>) => Promise<boolean>;
  removeMember: (id: number) => Promise<void>;
  updateMemberSortOrder: (id: number, sortOrder: number) => Promise<void>;
  
  addEvent: (event: Omit<Event, 'id' | 'sort_order'>) => Promise<boolean>;
  updateEvent: (id: number, event: Partial<Event>) => Promise<boolean>;
  removeEvent: (id: number) => Promise<void>;
  updateEventSortOrder: (id: number, sortOrder: number) => Promise<void>;

  addDiasporaCoordinator: (coordinator: Omit<DiasporaCoordinator, 'id' | 'sort_order'>) => Promise<boolean>;
  updateDiasporaCoordinator: (id: number, coordinator: Partial<DiasporaCoordinator>) => Promise<boolean>;
  removeDiasporaCoordinator: (id: number) => Promise<void>;
  updateDiasporaCoordinatorSortOrder: (id: number, sortOrder: number) => Promise<void>;

  uploadFile: (file: File, bucket?: string, onProgress?: (progress: number) => void) => Promise<string | null>;
  subscribeToRealtime: () => void;
  unsubscribeFromRealtime: () => void;
}

export const useStore = create<AppState>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      isLoading: false,
      hasHydrated: false,
      leaders: [],
      members: [],
      events: [],
      diasporaCoordinators: [],

      // Fetch data - simple and fast
      fetchData: async () => {
        // Stale-While-Revalidate: Only show loading if we have no data
        const hasData = get().members.length > 0;
        if (!hasData) {
          set({ isLoading: true });
        }

        const [leadersResult, membersResult, eventsResult, diasporaResult] = await Promise.all([
          supabase.from('leaders').select('*').order('sort_order', { ascending: true }),
          supabase.from('members').select('*').order('sort_order', { ascending: true }),
          supabase.from('events').select('*').order('sort_order', { ascending: true }),
          supabase.from('diaspora_coordinators').select('*').order('sort_order', { ascending: true })
        ]);

        if (leadersResult.data) set({ leaders: leadersResult.data });
        if (membersResult.data) set({ members: membersResult.data });
        if (eventsResult.data) {
          set({ 
            events: eventsResult.data.map((e: any) => ({
              ...e,
              images: e.images || (e.image ? [e.image] : [])
            })) 
          });
        }
        if (diasporaResult.data) set({ diasporaCoordinators: diasporaResult.data });
        
        set({ isLoading: false });
      },

      setHasHydrated: (state) => set({ hasHydrated: state }),

      login: async (email, password) => {
        if (!email || !password) {
          alert('Please enter both email and password');
          return false;
        }
        
        // Hardcoded admin check for easier access/testing
        if (email === 'admin@grassroots.com' && password === 'admin123') {
          set({ isAuthenticated: true });
          return true;
        }

        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (!error) {
          set({ isAuthenticated: true });
          return true;
        }
        alert('Login failed: ' + (error?.message || 'Invalid credentials'));
        return false;
      },

      logout: async () => {
        await supabase.auth.signOut();
        set({ isAuthenticated: false });
      },

      // LEADERS - New leaders go to bottom
      addLeader: async (leader) => {
        const currentLeaders = get().leaders;
        
        // Calculate next sort order (add to end)
        const maxSortOrder = currentLeaders.reduce((max, l) => Math.max(max, l.sort_order || 0), 0);
        const nextSortOrder = maxSortOrder + 1;
        
        // Insert new leader at calculated sort_order
        const { data, error } = await supabase.from('leaders').insert([{ ...leader, sort_order: nextSortOrder }]).select();
        
        if (!error && data) {
          // Optimistically update state
          set((state) => ({ leaders: [...state.leaders, data[0]] }));
          alert('Leader added successfully!');
          return true;
        } else {
          console.error('Error adding leader to Supabase:', error);
          alert('Failed to add leader to database: ' + (error?.message || 'Unknown error'));
          return false;
        }
      },

      updateLeader: async (id, leader) => {
        const { data, error } = await supabase.from('leaders').update(leader).eq('id', id).select();
        if (error) {
          console.error('Error updating leader:', error);
          alert('Failed to update leader: ' + error.message);
          return false;
        } else if (!data || data.length === 0) {
          console.error('Error updating leader: No data returned. Check RLS policies or if item exists.');
          alert('Failed to update leader. You might not have permission or the item was deleted.');
          return false;
        } else {
          set((state) => ({ 
            leaders: state.leaders.map(l => l.id === id ? data[0] : l)
          }));
          return true;
        }
      },

      removeLeader: async (id) => {
        const currentLeaders = get().leaders;
        set((state) => ({ leaders: state.leaders.filter((l) => l.id !== id) }));
        
        const { error, data } = await supabase.from('leaders').delete().eq('id', id).select();
        
        if (error || (data && data.length === 0)) {
          console.error('Error deleting leader from Supabase:', error || 'No rows deleted');
          set({ leaders: currentLeaders });
          alert('Failed to delete leader. You might not have permission.');
        } else {
          // Reorder remaining leaders to maintain sequential numbers
          const remainingLeaders = get().leaders;
          for (let i = 0; i < remainingLeaders.length; i++) {
            await supabase.from('leaders').update({ sort_order: i + 1 }).eq('id', remainingLeaders[i].id);
          }
          // Update local state sort orders
          set((state) => ({
            leaders: state.leaders.map((l, index) => ({ ...l, sort_order: index + 1 }))
          }));
        }
      },

      updateLeaderSortOrder: async (id, sortOrder) => {
        const { data, error } = await supabase.from('leaders').update({ sort_order: sortOrder }).eq('id', id).select();
        if (!error && data) {
          set((state) => ({ 
            leaders: state.leaders
              .map(l => l.id === id ? { ...l, sort_order: sortOrder } : l)
              .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
          }));
        } else {
          console.error('Error updating leader sort order:', error);
        }
      },

      // MEMBERS - New members go to bottom
      addMember: async (member) => {
        const currentMembers = get().members;
        
        // Calculate next sort order (add to end)
        const maxSortOrder = currentMembers.reduce((max, m) => Math.max(max, m.sort_order || 0), 0);
        const nextSortOrder = maxSortOrder + 1;
        
        // Insert new member at calculated sort_order
        const { data, error } = await supabase.from('members').insert([{ ...member, sort_order: nextSortOrder }]).select();
        
        if (!error && data) {
          // Optimistically update state
          set((state) => ({ members: [...state.members, data[0]] }));
          alert('Member added successfully!');
          return true;
        } else {
          console.error('Error adding member to Supabase:', error);
          alert('Failed to add member to database: ' + (error?.message || 'Unknown error'));
          return false;
        }
      },

      updateMember: async (id, member) => {
        const { data, error } = await supabase.from('members').update(member).eq('id', id).select();
        if (error) {
          console.error('Error updating member:', error);
          alert('Failed to update member: ' + error.message);
          return false;
        } else if (!data || data.length === 0) {
          console.error('Error updating member: No data returned. Check RLS policies or if item exists.');
          alert('Failed to update member. You might not have permission or the item was deleted.');
          return false;
        } else {
          set((state) => ({ 
            members: state.members.map(m => m.id === id ? data[0] : m)
          }));
          return true;
        }
      },

      removeMember: async (id) => {
        const currentMembers = get().members;
        set((state) => ({ members: state.members.filter((m) => m.id !== id) }));
        
        const { error, data } = await supabase.from('members').delete().eq('id', id).select();
        
        if (error || (data && data.length === 0)) {
          console.error('Error deleting member from Supabase:', error || 'No rows deleted');
          set({ members: currentMembers });
          alert('Failed to delete member. You might not have permission.');
        } else {
          // Reorder remaining members to maintain sequential numbers
          const remainingMembers = get().members;
          for (let i = 0; i < remainingMembers.length; i++) {
            await supabase.from('members').update({ sort_order: i + 1 }).eq('id', remainingMembers[i].id);
          }
          set((state) => ({
            members: state.members.map((m, index) => ({ ...m, sort_order: index + 1 }))
          }));
        }
      },

      updateMemberSortOrder: async (id, sortOrder) => {
        const { data, error } = await supabase.from('members').update({ sort_order: sortOrder }).eq('id', id).select();
        if (!error && data) {
          set((state) => ({ 
            members: state.members
              .map(m => m.id === id ? { ...m, sort_order: sortOrder } : m)
              .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
          }));
        } else {
          console.error('Error updating member sort order:', error);
        }
      },

      // EVENTS - New events go to bottom
      addEvent: async (event) => {
        const currentEvents = get().events;
        
        // Calculate next sort order (add to end)
        const maxSortOrder = currentEvents.reduce((max, e) => Math.max(max, e.sort_order || 0), 0);
        const nextSortOrder = maxSortOrder + 1;
        
        // Insert new event at calculated sort_order
        // Also save first image to 'image' column for backward compatibility if needed by DB
        const { data, error } = await supabase.from('events').insert([{ ...event, image: event.images[0] || '', sort_order: nextSortOrder }]).select();
        
        if (!error && data) {
          // Optimistically update state
          set((state) => {
             const newEvent = {
                ...data[0],
                images: data[0].images || (data[0].image ? [data[0].image] : [])
             };
             return {
                 events: [...state.events, newEvent]
             };
          });
          alert('Event added successfully!');
          return true;
        } else {
          console.error('Error adding event to Supabase:', error);
          alert('Failed to add event to database: ' + (error?.message || 'Unknown error'));
          return false;
        }
      },

      updateEvent: async (id, event) => {
        // Also update 'image' column for backward compatibility
        const updateData = { ...event, image: event.images?.[0] };
        const { data, error } = await supabase.from('events').update(updateData).eq('id', id).select();
        if (error) {
          console.error('Error updating event:', error);
          alert('Failed to update event: ' + error.message);
          return false;
        } else if (!data || data.length === 0) {
          console.error('Error updating event: No data returned. Check RLS policies or if item exists.');
          alert('Failed to update event. You might not have permission or the item was deleted.');
          return false;
        } else {
          set((state) => ({ 
            events: state.events.map(e => e.id === id ? { ...data[0], images: data[0].images || (data[0].image ? [data[0].image] : []) } : e)
          }));
          return true;
        }
      },

      removeEvent: async (id) => {
        const currentEvents = get().events;
        set((state) => ({ events: state.events.filter((e) => e.id !== id) }));
        
        const { error, data } = await supabase.from('events').delete().eq('id', id).select();
        
        if (error || (data && data.length === 0)) {
          console.error('Error deleting event from Supabase:', error || 'No rows deleted');
          set({ events: currentEvents });
          alert('Failed to delete event. You might not have permission.');
        } else {
          // Reorder remaining events to maintain sequential numbers
          const remainingEvents = get().events;
          for (let i = 0; i < remainingEvents.length; i++) {
            await supabase.from('events').update({ sort_order: i + 1 }).eq('id', remainingEvents[i].id);
          }
          set((state) => ({
            events: state.events.map((e, index) => ({ ...e, sort_order: index + 1 }))
          }));
        }
      },

      updateEventSortOrder: async (id, sortOrder) => {
        const { data, error } = await supabase.from('events').update({ sort_order: sortOrder }).eq('id', id).select();
        if (!error && data) {
          set((state) => ({ 
            events: state.events
              .map(e => e.id === id ? { ...e, sort_order: sortOrder } : e)
              .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
          }));
        } else {
          console.error('Error updating event sort order:', error);
        }
      },

      // DIASPORA COORDINATORS
      addDiasporaCoordinator: async (coordinator) => {
        const current = get().diasporaCoordinators;
        const maxSortOrder = current.reduce((max, c) => Math.max(max, c.sort_order || 0), 0);
        const nextSortOrder = maxSortOrder + 1;
        
        const { data, error } = await supabase.from('diaspora_coordinators').insert([{ ...coordinator, sort_order: nextSortOrder }]).select();
        
        if (!error && data) {
          set((state) => ({ diasporaCoordinators: [...state.diasporaCoordinators, data[0]] }));
          alert('Diaspora Coordinator added successfully!');
          return true;
        } else {
          console.error('Error adding diaspora coordinator:', error);
          alert('Failed to add: ' + (error?.message || 'Unknown error'));
          return false;
        }
      },

      updateDiasporaCoordinator: async (id, coordinator) => {
        const { data, error } = await supabase.from('diaspora_coordinators').update(coordinator).eq('id', id).select();
        if (error) {
          console.error('Error updating diaspora coordinator:', error);
          alert('Failed to update: ' + error.message);
          return false;
        } else {
          set((state) => ({ 
            diasporaCoordinators: state.diasporaCoordinators.map(c => c.id === id ? data[0] : c)
          }));
          return true;
        }
      },

      removeDiasporaCoordinator: async (id) => {
        const current = get().diasporaCoordinators;
        set((state) => ({ diasporaCoordinators: state.diasporaCoordinators.filter((c) => c.id !== id) }));
        
        const { error } = await supabase.from('diaspora_coordinators').delete().eq('id', id);
        
        if (error) {
          console.error('Error deleting diaspora coordinator:', error);
          set({ diasporaCoordinators: current });
          alert('Failed to delete. You might not have permission.');
        } else {
          // Reorder remaining
          const remaining = get().diasporaCoordinators;
          for (let i = 0; i < remaining.length; i++) {
            await supabase.from('diaspora_coordinators').update({ sort_order: i + 1 }).eq('id', remaining[i].id);
          }
          set((state) => ({
            diasporaCoordinators: state.diasporaCoordinators.map((c, index) => ({ ...c, sort_order: index + 1 }))
          }));
        }
      },

      updateDiasporaCoordinatorSortOrder: async (id, sortOrder) => {
        const { data, error } = await supabase.from('diaspora_coordinators').update({ sort_order: sortOrder }).eq('id', id).select();
        if (!error && data) {
          set((state) => ({ 
            diasporaCoordinators: state.diasporaCoordinators
              .map(c => c.id === id ? { ...c, sort_order: sortOrder } : c)
              .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
          }));
        } else {
          console.error('Error updating sort order:', error);
        }
      },

      uploadFile: async (file, bucket = 'IMAGES', onProgress) => {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from(bucket)
          .upload(filePath, file, {
            contentType: file.type,
            // @ts-ignore
            onUploadProgress: (progress: any) => {
              if (onProgress) {
                const percentage = (progress.loaded / progress.total) * 100;
                onProgress(Math.round(percentage));
              }
            },
          });

        if (uploadError) {
          console.error('Error uploading file:', uploadError);
          return null;
        }

        const { data } = supabase.storage.from(bucket).getPublicUrl(filePath);
        return data.publicUrl;
      },

      subscribeToRealtime: () => {
        supabase.channel('public:db_changes')
          .on(
            'postgres_changes',
            { event: '*', schema: 'public' },
            (payload) => {
              const { table, eventType, new: newRecord, old: oldRecord } = payload;

              const handleUpdate = (list: any[], newItem: any, oldItem: any) => {
                let newList = [...list];
                if (eventType === 'INSERT') {
                  // Prevent duplicates if optimistic update already added it
                  if (!newList.some(i => i.id === newItem.id)) {
                    newList.push(newItem);
                  }
                } else if (eventType === 'UPDATE') {
                  newList = newList.map(i => i.id === newItem.id ? newItem : i);
                } else if (eventType === 'DELETE') {
                  newList = newList.filter(i => i.id !== oldItem.id);
                }
                // Maintain sort order
                return newList.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
              };

              set((state) => {
                if (table === 'leaders') {
                  return { leaders: handleUpdate(state.leaders, newRecord, oldRecord) };
                }
                if (table === 'members') {
                  return { members: handleUpdate(state.members, newRecord, oldRecord) };
                }
                if (table === 'events') {
                  // Normalize event images structure
                  let processedRecord = newRecord;
                  if (eventType !== 'DELETE') {
                    processedRecord = {
                      ...newRecord,
                      images: newRecord.images || (newRecord.image ? [newRecord.image] : [])
                    };
                  }
                  return { events: handleUpdate(state.events, processedRecord, oldRecord) };
                }
                if (table === 'diaspora_coordinators') {
                  return { diasporaCoordinators: handleUpdate(state.diasporaCoordinators, newRecord, oldRecord) };
                }
                return state;
              });
            }
          )
          .subscribe();
      },

      unsubscribeFromRealtime: () => {
        supabase.removeAllChannels();
      },
    }),
    {
      name: 'bot-storage',
      // Custom storage wrapper to catch QuotaExceededError
      storage: createJSONStorage(() => ({
        getItem: (name) => {
          try {
            return localStorage.getItem(name);
          } catch (e) {
            return null;
          }
        },
        setItem: (name, value) => {
          try {
            localStorage.setItem(name, value);
          } catch (e) {
            console.warn('LocalStorage quota exceeded, failed to save state.');
          }
        },
        removeItem: (name) => localStorage.removeItem(name),
      })),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        leaders: state.leaders.filter(l => !!l).map((leader) => ({
          ...leader,
          // If image is a base64 string (starts with data:), don't save it to localStorage
          image: leader.image && leader.image.startsWith('data:') ? '' : leader.image
        })),
        members: state.members.filter(m => !!m).map((member) => ({
          ...member,
          // If image is a base64 string (starts with data:), don't save it to localStorage
          image: member.image && member.image.startsWith('data:') ? '' : member.image
        })),
        events: state.events.filter(e => !!e).map((event) => ({
          ...event,
          // If image is a base64 string (starts with data:), don't save it to localStorage
          images: event.images ? event.images.map(img => img.startsWith('data:') ? '' : img) : []
        })),
        diasporaCoordinators: state.diasporaCoordinators.filter(d => !!d).map((coord) => ({
          ...coord,
          image: coord.image && coord.image.startsWith('data:') ? '' : coord.image
        })),
      }),
    }
  )
);
